import type {QueryClient} from "@tanstack/react-query";

import {NOTIFICATION_FEED_QUERY_KEY} from "@/src/modules/notification-feed/hooks/use-notification-feed";
import {optimisticClearShiftComplianceNotifications} from "@/src/modules/notification-feed/lib/optimistic-clear-shift-compliance-notifications";

export type ReconcileComplianceOptions = {
  /** Remove matching `shift.break_alert` rows before refetch (handled break actions). */
  shiftBreakId?: number;
  /** Remove matching `shift.end_alert` rows before refetch (shift end / extend). */
  shiftId?: number;
};

/**
 * Refetch the authoritative compliance sources, in parallel, then resolve.
 * Callers wrap this in a `setReconciling(true/false)` beat. Keys are prefixes
 * (TanStack matches by prefix), so param-keyed queries are covered.
 *
 * Also invalidates the v2 notification feed so the bell loses stale shift rows
 * when the officer handles break/end obligations (server discard may lag or be missed on socket).
 *
 * Intentionally excludes officer home `useScheduledShifts` (`shifts` limit=4 /
 * `filter[upcoming]`) — gates only need the active row; upcoming history refreshes
 * on shift start/end via explicit `invalidate("shifts")`.
 */
export function reconcileCompliance(
  queryClient: QueryClient,
  options?: ReconcileComplianceOptions,
): Promise<unknown> {
  if (options?.shiftBreakId != null || options?.shiftId != null) {
    optimisticClearShiftComplianceNotifications(options);
  }

  return Promise.all([
    queryClient.refetchQueries({queryKey: ["active-shift"]}),
    queryClient.refetchQueries({queryKey: ["briefingboard-pending"]}),
    queryClient.invalidateQueries({queryKey: [NOTIFICATION_FEED_QUERY_KEY]}),
  ]);
}
